import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  GlobalStyles,
  Button,
  AppBar,
  Toolbar,
  Paper,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import { Play, Pause, Save, Download, Waves } from 'lucide-mui';
import { getSharedFile } from '../../services/fileSystemService';
import Loader from '../app/loader/LoaderSvg';
import DescriptionWithClickableTimestamps from '../selectedVideo/tabs/summary/summaryViewOnly/DescriptionWithClickableTimestamps';
import { onClickTimestamp } from '../../lib/commonUtils';
import DefaultThumbnail from '../../assets/recording/DefaultThumbnail.svg';
import LogoPlaceholder from '../../assets/icons/new_logo.svg';
import Chapters from '../selectedVideo/tabs/chapters';

interface SharedFile {
  file: {
    url: string;
    name: string;
    description?: string;
    thumbUrl?: string;
    streams?: string[];
  };
  transcript?: any;
  postProcess?: {
    systemPromptResponses?: {
      CHAPTERS?: {
        responseText: string;
      };
    };
    userPromptResponses?: Record<string, any>;
  };
}

const forceMediaInit = async (element: HTMLMediaElement) => {
  try {
    const mediaElement = element;
    mediaElement.currentTime = 0;
    await mediaElement.load();
    const playPromise = mediaElement.play();
    if (playPromise !== undefined) {
      await playPromise;
      mediaElement.pause();
    }
  } catch (error) {
    console.log('Media initialization attempted:', error);
  }
};

const SharedVideo: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const location = useLocation();
  const [file, setFile] = useState<SharedFile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const [showSummary, setShowSummary] = useState<boolean>(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const [isAudioOnly, setIsAudioOnly] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [showControls, setShowControls] = React.useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile =
    /Mobi|Android/i.test(navigator.userAgent) || window.innerWidth < 768;

  const searchParams = new URLSearchParams(location.search);
  const isEmbed = searchParams.get('embed') === 'true';
  const start = parseFloat(searchParams.get('start') || '0');
  const end = parseFloat(searchParams.get('end') || '0');
  const isClipView = start >= 0 && end > start;

  useEffect(() => {
    const fetchSharedFile = async () => {
      setIsLoading(true);
      try {
        const sharedFile = await getSharedFile(shareId!);
        const fileObj = sharedFile.file;
        if (fileObj?.url) {
          const share: SharedFile = {
            file: {
              url: fileObj.url,
              name: fileObj.name,
              description: fileObj.description,
              thumbUrl: fileObj.thumbUrl,
              streams: fileObj.streams,
            },
            transcript: sharedFile.transcript,
            postProcess: sharedFile.postProcess,
          };
          setFile(share);
          setThumbnail(fileObj.thumbUrl || DefaultThumbnail);
          setIsAudioOnly(
            fileObj.streams?.length === 1 && fileObj.streams[0] === 'audio'
          );
          setIsLoading(false);
        } else {
          throw new Error('Video not found or unable to retrieve file URL.');
        }
      } catch (err) {
        setError('An error occurred while fetching the video.');
        setIsLoading(false);
      }
    };

    if (shareId && shareId.length > 10) {
      window.location.replace(
        `${window.location.origin}/legacy/#/shared/${shareId}`
      );
    } else {
      fetchSharedFile();
    }
  }, [shareId]);

  useEffect(() => {
    if (isEmbed && videoRef.current && containerRef.current) {
      const updateLayout = () => {
        const video = videoRef.current;
        const container = containerRef.current;
        if (video && container) {
          setShowSummary(false);
        }
      };

      const resizeObserver = new ResizeObserver(updateLayout);
      resizeObserver.observe(containerRef.current);

      const video = videoRef.current;
      video.addEventListener('loadedmetadata', updateLayout);

      return () => {
        resizeObserver.disconnect();
        video.removeEventListener('loadedmetadata', updateLayout);
      };
    }
    return undefined;
  }, [isEmbed]);

  useEffect(() => {
    if (videoRef.current && file) {
      const mediaElement = videoRef.current;

      // Try to play unmuted on load
      const attemptPlay = async () => {
        try {
          if (isClipView || (isAudioOnly && start >= 0 && end > start)) {
            mediaElement.currentTime = start;
          }
          await mediaElement.play();
          setIsPlaying(true);
        } catch (playError) {
          console.log('Autoplay failed:', playError);
        }
      };

      // Force initialization
      forceMediaInit(mediaElement);
      attemptPlay();

      const handleTimeUpdate = () => {
        const video = videoRef.current;
        if (video) {
          if (isClipView || (isAudioOnly && start >= 0 && end > start)) {
            if (video.currentTime >= end) {
              video.pause();
              video.currentTime = start;
              setIsPlaying(false);
            }
            const clipProgress =
              ((video.currentTime - start) / (end - start)) * 100;
            setVideoProgress(clipProgress);
          } else {
            setVideoProgress((video.currentTime / video.duration) * 100);
          }
        }
      };

      const handlePlay = () => setIsPlaying(true);
      const handlePause = () => setIsPlaying(false);

      mediaElement.addEventListener('timeupdate', handleTimeUpdate);
      mediaElement.addEventListener('play', handlePlay);
      mediaElement.addEventListener('pause', handlePause);

      return () => {
        if (mediaElement) {
          mediaElement.removeEventListener('timeupdate', handleTimeUpdate);
          mediaElement.removeEventListener('play', handlePlay);
          mediaElement.removeEventListener('pause', handlePause);
        }
      };
    }
    return undefined;
  }, [file, videoRef.current, start, end, isClipView, isAudioOnly]);

  useEffect(() => {
    const updateMetaTags = () => {
      const title = file?.file?.name || 'Shared Video';
      const description = file?.file?.description || 'Watch this shared video';
      const image = thumbnail || DefaultThumbnail;

      document.title = title;

      const metaTags = {
        'og:title': title,
        'og:description': description,
        'og:image': image,
        'og:url': window.location.href,
        'og:type': 'video.other',
      };

      Object.entries(metaTags).forEach(([property, content]) => {
        let metaTag = document.querySelector(`meta[property="${property}"]`);
        if (!metaTag) {
          metaTag = document.createElement('meta');
          metaTag.setAttribute('property', property);
          document.head.appendChild(metaTag);
        }
        metaTag.setAttribute('content', content);
      });
    };

    updateMetaTags();

    return () => {
      ['og:title', 'og:description', 'og:image', 'og:url', 'og:type'].forEach(
        (property) => {
          const metaTag = document.querySelector(
            `meta[property="${property}"]`
          );
          if (metaTag) {
            metaTag.remove();
          }
        }
      );
    };
  }, [file, thumbnail]);

  const handleRedirect = () => {
    if (file?.file?.url) {
      const url = `https://screenapp.io/app/#/dashboard?importUrl=${encodeURIComponent(
        file.file.url
      )}`;
      window.open(url, '_blank');
    }
  };

  // Add new handlers for video controls
  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const renderContent = () => {
    if (!file) return null;

    return (
      <CardContent
        ref={descriptionRef}
        sx={{
          p: 2,
          flexGrow: 1,
          overflowY: 'auto',
          backgroundColor: isEmbed ? 'transparent' : undefined,
          display: isClipView ? 'none' : 'block', // Hide summary in clip view
        }}
      >
        <Typography variant="h5" component="h1" gutterBottom>
          {isClipView ? `${file.file.name} - Clip` : file.file.name}
        </Typography>
        <Box
          onClick={(e) => {
            const target = e.target as HTMLElement;
            if (target.tagName === 'SPAN' && target.dataset.timestamp) {
              onClickTimestamp(parseFloat(target.dataset.timestamp));
            }
          }}
        >
          {file.postProcess?.systemPromptResponses?.CHAPTERS ? (
            <Box sx={{ mx: -2 }}>
              <Chapters
                chaptersData={JSON.parse(
                  file.postProcess.systemPromptResponses.CHAPTERS.responseText
                )}
                videoRef={videoRef}
                allowLocking={false}
              />
            </Box>
          ) : (
            <DescriptionWithClickableTimestamps
              text={file.file.description || ''}
            />
          )}
        </Box>
      </CardContent>
    );
  };

  const renderSimpleControls = () => {
    // Show controls for clips or audio files with start/end parameters
    if (!isClipView && !(isAudioOnly && start >= 0 && end > start)) return null;

    const circumference = 2 * Math.PI * 38;
    const progressOffset = circumference * (1 - videoProgress / 100);

    return (
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          opacity: showControls || !isPlaying || isAudioOnly ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
          zIndex: 2,
          width: 80,
          height: 80,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'auto',
        }}
      >
        {/* Circular progress */}
        <svg
          width="80"
          height="80"
          viewBox="0 0 80 80"
          style={{
            position: 'absolute',
            transform: 'rotate(-90deg)',
          }}
        >
          {/* White progress circle (underneath) */}
          <circle
            cx="40"
            cy="40"
            r="38"
            fill="none"
            stroke="white"
            strokeWidth="3"
            strokeDasharray={circumference}
            strokeDashoffset={progressOffset}
            style={{
              transition: 'stroke-dashoffset 0.1s linear',
            }}
          />
          {/* White overlay circle */}
          <circle
            cx="40"
            cy="40"
            r="38"
            fill="none"
            stroke="white"
            strokeWidth="3"
            strokeOpacity={0.3}
          />
        </svg>

        {/* Play/Pause button */}
        <IconButton
          onClick={handlePlayPause}
          sx={{
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            color: 'white',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              transform: 'scale(1.1)',
              '& .MuiSvgIcon-root': {
                color: 'white',
              },
            },
            width: 60,
            height: 60,
            zIndex: 3,
          }}
          size="large"
        >
          {isPlaying ? (
            <Pause sx={{ fontSize: 40, color: 'white' }} />
          ) : (
            <Play sx={{ fontSize: 40, color: 'white' }} />
          )}
        </IconButton>
      </Box>
    );
  };

  const renderAudioPlaceholder = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          width: '100%',
          bgcolor: 'background.paper',
        }}
      >
        <Waves
          sx={{
            fontSize: '48px',
            color: 'secondary.main',
            opacity: isPlaying ? 1 : 0.6,
            animation: isPlaying ? 'pulse 1s ease-in-out infinite' : 'none',
            '@keyframes pulse': {
              '0%': { transform: 'scale(1)' },
              '50%': { transform: 'scale(1.1)' },
              '100%': { transform: 'scale(1)' },
            },
          }}
        />
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Box sx={{ width: '3rem', height: '3rem' }}>
          <Loader />
        </Box>
      </Box>
    );
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <>
      <GlobalStyles
        styles={(globalTheme) => ({
          body: {
            margin: 0,
            backgroundColor: globalTheme.palette.background.default,
            overflow: 'auto',
          },
        })}
      />
      {!isEmbed && !isClipView && (
        <AppBar
          position="static"
          component={Paper}
          sx={{
            backgroundColor: 'background.paper',
            borderBottom: 1,
            borderColor: 'secondary.light',
            mb: 2,
          }}
        >
          <Toolbar>
            <Box
              component="a"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleRedirect();
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
              }}
            >
              <img
                src={LogoPlaceholder}
                alt="ScreenApp"
                style={{
                  width: 32,
                  height: 'auto',
                }}
              />
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleRedirect}
              startIcon={<Save />}
              sx={{ mr: 1 }}
            >
              Save
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleRedirect}
              startIcon={<Download />}
              sx={{ mr: 1 }}
            >
              Download
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleRedirect}
            >
              Start Free
            </Button>
          </Toolbar>
        </AppBar>
      )}

      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          height: isClipView ? '100vh' : 'auto',
          margin: isEmbed ? 0 : { md: '0 auto', xs: 0 },
          maxWidth: isEmbed ? '100%' : { md: '60%', xs: '100%' },
          display: 'flex',
          flexDirection: 'column',
          ...(isEmbed && {
            alignItems: 'center',
            minHeight: '100vh',
          }),
          ...(isClipView && {
            alignItems: 'center',
            justifyContent: 'center',
          }),
        }}
      >
        {file?.file && (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              ...(isClipView && {
                mb: 0, // Remove bottom margin in clip mode
              }),
            }}
          >
            <Box
              sx={{
                position: 'relative',
                '&:hover': {
                  '& .video-controls': {
                    opacity: 1,
                  },
                },
              }}
              onMouseEnter={() => !isMobile && setShowControls(true)}
              onMouseLeave={() => !isMobile && setShowControls(false)}
            >
              <CardMedia
                component="video"
                id="video-element"
                ref={videoRef}
                sx={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                  maxWidth: '100%',
                  minHeight: isAudioOnly ? '200px' : '200px',
                  backgroundColor: isAudioOnly ? 'background.paper' : '#000',
                  borderRadius: isEmbed || isSmallScreen ? 0 : '8px',
                  mb: isClipView ? 0 : 2,
                  ...(isEmbed && {
                    minHeight: 'unset',
                    maxHeight: 'unset',
                  }),
                }}
                src={`${file.file.url}#t=${start},${end || ''}`}
                controls={!isClipView}
                playsInline
                webkit-playsinline="true"
                onClick={isMobile ? undefined : handlePlayPause}
              />
              {isAudioOnly &&
                start >= 0 &&
                end > start &&
                renderAudioPlaceholder()}
              {renderSimpleControls()}
            </Box>
          </Box>
        )}
        {(!isEmbed || showSummary) && (
          <Card
            sx={{
              borderRadius: isEmbed || isSmallScreen ? 0 : '8px',
              border: '1px solid',
              borderColor: 'secondary.light',
            }}
          >
            {renderContent()}
          </Card>
        )}
      </Box>
    </>
  );
};

export default SharedVideo;
